import { ApolloQueryResult } from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, { useEffect, useState } from 'react';
import { ITableActions } from 'apps/things/app/item/context/ItemProvider';


import useOVSPagination from 'hooks/useOVSPagination';
import { funNumberAgr } from '@types';
import { GetAllServicersResponse_page_edges } from '../types/GetAllServicersResponse';
import { useLazyGetAllServicersQuery } from '../queries';
import { GetAllServicers, GetAllServicersVariables } from '../types/GetAllServicers';
import { PAGE_LIST_LIMIT } from '@constants';

export interface IServicerContext {
  items: GetAllServicersResponse_page_edges[];
  setItems: (item: GetAllServicersResponse_page_edges[]) => void;
  loading: boolean;
  getItems: () => void;
  activeStage: number;
  setActiveStage: (stage: number) => void;
  tableActions: ITableActions[];
  refetchItems:
  | ((
    variables?: Partial<GetAllServicersVariables> | undefined
  ) => Promise<ApolloQueryResult<GetAllServicers>>)
  | undefined;
  goTo: (nextPrev: boolean) => void;
  paging: any;
  setPageLimit: funNumberAgr
}

export const ServicerContext = React.createContext(
  {} as IServicerContext
);

const ServicerProvider: React.FC<IProps> = ({ children }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [item, setItem] = React.useState(
    {} as GetAllServicersResponse_page_edges
  );
  const [items, setItems] = React.useState<
    GetAllServicersResponse_page_edges[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /// hooks
  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging, pageListLimit, setPageListLimit
  } = useOVSPagination();

  const [
    getItems,
    { refetch: refetchItems, data, loading, fetchMore },
  ] = useLazyGetAllServicersQuery({
    first: PAGE_LIST_LIMIT,
  });

  useEffect(() => {
    setItems(data?.getAllServicers?.page?.edges || []);
    setPaging({
      ...data?.getAllServicers?.pageData,
      ...data?.getAllServicers?.page?.pageInfo,
    });
    // eslint-disable-next-line
  }, [data]);

  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllServicersVariables = {
      first: pageListLimit,
      ...initPagAction(next),
    };

    if (fetchMore) {
      setIsLoadingMore(true);
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllServicers?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllServicers?.pageData,
        ..._data?.data?.getAllServicers?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };

  const setPageLimit = (limit: number) => {
    setPageListLimit(limit)
    setTimeout(() => {
      refetchItems && refetchItems()
    }, 100);

  }

  const value = React.useMemo(
    () => ({
      item,
      setItem,
      items,
      setItems,
      loading: loading,
      getItems: getItems,
      refetchItems,
      tableActions: [],
      activeStage,
      setActiveStage,
      paging,
      goTo, setPageLimit
    }), // eslint-disable-next-line
    [items, loading, paging]
  );

  return (
    <ServicerContext.Provider value={value}>{children}</ServicerContext.Provider>
  );
};

export default ServicerProvider;
